import type { ParagraphTechStack as ParagraphTechStackData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphTechStack.module.css";

/**
 * Layout 37 - `PARAGRAPH_TECH_STACK_LAYOUT`: stack technologii.
 *
 * Struktura:
 *   - Eyebrow (text_1) - uppercase label nad tytulem ("TECHNOLOGIE")
 *   - Title h2 (name) - "Na czym budujemy"
 *   - Lead (text) - opis pod tytulem (HTML)
 *   - Grid ikon technologii (dynamiczna liczba z gallery):
 *       - Kazdy tile = ikona (PNG/SVG upload) + nazwa techa (description)
 *   - Subheading (text_2) - "MODELE AI, Z KTORYMI PRACUJEMY"
 *   - Grid 3 kart AI modeli:
 *       - Ikona (upload) + nazwa modelu
 */
export function ParagraphTechStack({
  paragraph,
}: {
  paragraph: ParagraphTechStackData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: lead,
    text_2: aiSubheading,
    technologies,
    ai_models,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasTech = Array.isArray(technologies) && technologies.length > 0;
  const hasAi = Array.isArray(ai_models) && ai_models.length > 0;
  const hasAiSubheading = aiSubheading && aiSubheading.trim() !== "";

  return (
    <section className={styles.section}>
      {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
      {title ? <h2 className={styles.title}>{title}</h2> : null}
      {hasLead ? (
        <HtmlText html={lead} className={styles.lead} />
      ) : null}

      {hasTech ? (
        <div className={styles.techGrid}>
          {technologies.map((tech) => (
            <div key={tech.id ?? tech.icon_url} className={styles.techTile}>
              {tech.icon_url ? (
                <img
                  src={tech.icon_url}
                  alt={tech.name}
                  className={styles.techIcon}
                  loading="lazy"
                />
              ) : null}
              {tech.name ? <span className={styles.techName}>{tech.name}</span> : null}
            </div>
          ))}
        </div>
      ) : null}

      {hasAiSubheading || hasAi ? (
        <>
          {hasAiSubheading ? (
            <p className={styles.aiSubheading}>{aiSubheading}</p>
          ) : null}
          {hasAi ? (
            <div className={styles.aiGrid}>
              {ai_models.map((ai, idx) => (
                <div key={idx} className={styles.aiCard}>
                  {ai.icon_url ? (
                    <img
                      src={ai.icon_url}
                      alt={ai.name}
                      className={styles.aiIcon}
                      loading="lazy"
                    />
                  ) : null}
                  {ai.name ? <span className={styles.aiName}>{ai.name}</span> : null}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
